import { supabase } from "@/integrations/supabase/client";
import { roundCoord } from "@/lib/resolvePlaceName";

export type CityWaitlistSource = "browse" | "home" | "onboarding";

const DEVICE_ID_KEY = "grounded_device_id";

/**
 * Best effort per device identity for anonymous votes. Not a security control:
 * it only prevents the same browser from voting twice for the same city.
 */
export function getDeviceId(): string | null {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
  } catch {
    return null;
  }
}

/** The place a vote is for, as stored on every city_waitlist row. */
export interface WaitlistPlace {
  searched_city: string;
  city: string | null;
  state: string | null;
  lat: number;
  lng: number;
}

/**
 * The single city_waitlist insert. The table is insert only by design, so an
 * email or account follow up writes a NEW row rather than updating the vote.
 */
export async function insertCityWaitlistRow(args: {
  place: WaitlistPlace;
  email: string | null;
  userId: string | null;
  source: CityWaitlistSource;
}) {
  const { place, email, userId, source } = args;
  const { error } = await supabase.from("city_waitlist").insert({
    email,
    user_id: userId,
    device_id: getDeviceId(),
    searched_city: place.searched_city,
    lat: place.lat,
    lng: place.lng,
    city: place.city,
    state: place.state,
    lat_rounded: roundCoord(place.lat),
    lng_rounded: roundCoord(place.lng),
    source,
  });
  return error;
}

/** Event properties every city_waitlist event carries. */
export function waitlistEventProps(place: WaitlistPlace, source: CityWaitlistSource) {
  return {
    searched_city: place.searched_city,
    city: place.city,
    state: place.state,
    lat: roundCoord(place.lat),
    lng: roundCoord(place.lng),
    source,
  };
}
